import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';

/* MSK images served from public/images */
const heroSlides = [
  { src: '/images/MSK_4.jpeg', alt: 'MSK Prasad - BCCI Chief Selector' },
  { src: '/images/MSK_6.jpeg', alt: 'MSK Prasad in BCCI blazer' },
  { src: '/images/MSK_9.jpeg', alt: 'MSK Prasad with Indian cricket team' },
  { src: '/images/MSK_18.jpeg', alt: 'MSK Prasad at official event' },
  { src: '/images/MSK_10.jpeg', alt: 'MSK Prasad with Virat Kohli' },
];

const galleryImages = [
  { src: '/images/MSK_4.jpeg', alt: 'MSK Prasad coaching session', quote: '"MSK Prasad has an exceptional eye for talent. His selections shaped modern Indian cricket."', author: 'Sourav Ganguly' },
  { src: '/images/MSK_5.jpeg', alt: 'Academy training day', quote: '"MSK\'s dedication to grassroots cricket is unmatched. He is building champions from the ground up."', author: 'VVS Laxman' },
  { src: '/images/MSK_6.jpeg', alt: 'MSK Prasad in BCCI blazer', quote: '"A true gentleman of the game — MSK Prasad brings integrity and vision to everything he does."', author: 'Rahul Dravid' },
  { src: '/images/MSK_7.jpeg', alt: 'Practice session', quote: '"MSK\'s coaching philosophy nurtures not just skills, but character and discipline in young players."', author: 'Anil Kumble' },
  { src: '/images/MSK_8.jpeg', alt: 'Young cricketers training', quote: '"The way MSK mentors young cricketers is inspiring. He sees potential where others see beginners."', author: 'Sachin Tendulkar' },
  { src: '/images/MSK_9.jpeg', alt: 'MSK Prasad with Indian cricket team', quote: '"MSK\'s vision as Chief Selector gave India a squad that conquered the world."', author: 'Ravi Shastri' },
  { src: '/images/MSK_10.jpeg', alt: 'MSK Prasad with Virat Kohli', quote: '"MSK Prasad believed in my abilities as captain. His faith made a difference."', author: 'Virat Kohli' },
  { src: '/images/MSK_11.jpeg', alt: 'Cricket coaching in action', quote: '"MSK brings international expertise to local talent — that\'s what makes his academy extraordinary."', author: 'Venkatesh Prasad' },
  { src: '/images/MSK_14.jpeg', alt: 'Academy group photo', quote: '"His commitment to youth cricket development in the US is paving the way for future champions."', author: 'Javagal Srinath' },
  { src: '/images/MSK_15.jpeg', alt: 'Batting practice', quote: '"MSK\'s knowledge of the game is encyclopedic. Learning from him is a privilege."', author: 'Harbhajan Singh' },
  { src: '/images/MSK_16.jpeg', alt: 'Bowling drills', quote: '"MSK Prasad understands the mental side of cricket like few others. He builds confident players."', author: 'Sunil Gavaskar' },
  { src: '/images/MSK_17.jpeg', alt: 'Team huddle', quote: '"The passion MSK brings to coaching is contagious. His players learn to love the game deeply."', author: 'Kapil Dev' },
  { src: '/images/MSK_18.jpeg', alt: 'MSK Prasad at official event', quote: '"MSK\'s ability to identify and nurture talent is a rare gift. Indian cricket owes him a great deal."', author: 'Mohammad Azharuddin' },
  { src: '/images/MSK_19.jpeg', alt: 'Academy outdoor session', quote: '"From test cricket to coaching, MSK Prasad continues to shape the beautiful game of cricket."', author: 'Irfan Pathan' },
];

const coaches = [
  {
    name: 'Tonito Willett',
    role: 'Senior Cricket Coach',
    bio: 'Former West Indies international cricketer with extensive coaching experience at the highest level.',
    tags: ['West Indies', 'International', 'Batting'],
  },
  {
    name: 'Khawar Ali',
    role: 'All-Rounder Coach',
    bio: 'Oman national team all-rounder who competed in ICC World T20 and World Cup Qualifiers.',
    tags: ['Oman', 'All-Rounder', 'T20'],
  },
  {
    name: 'Zeeshan Maqsood',
    role: 'High Performance Coach',
    bio: 'Captain of the Oman national cricket team with multiple ICC World Cup Qualifier appearances.',
    tags: ['Oman Captain', 'Strategy', 'Leadership'],
  },
  {
    name: 'Coach Vinay Gupta',
    role: 'Development Coach',
    bio: 'Experienced youth cricket coach focused on building strong fundamentals and nurturing young talent.',
    tags: ['Youth Development', 'Fundamentals', 'Mentoring'],
  },
];

const HomePage = () => {
  /* Hero slideshow state */
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);
  /* Gallery slideshow state */
  const [currentGallerySlide, setCurrentGallerySlide] = useState(0);
  const [galleryPaused, setGalleryPaused] = useState(false);
  /* Gallery lightbox state */
  const [lightboxIndex, setLightboxIndex] = useState(null);

  /* Auto-advance hero slideshow */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  /* Auto-advance gallery slideshow */
  useEffect(() => {
    if (galleryPaused) return;
    const timer = setInterval(() => {
      setCurrentGallerySlide((prev) => (prev + 1) % galleryImages.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [galleryPaused]);

  /* Close lightbox on Escape key */
  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex((prev) => (prev + 1) % galleryImages.length);
      if (e.key === 'ArrowLeft') setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex]);

  return (
    <div className="homepage">
      {/* ====== HERO ====== */}
      <section className="hero-section">
        <div className="hero-slideshow">
          {heroSlides.map((slide, idx) => (
            <div key={idx} className={`hero-slide ${idx === currentHeroSlide ? 'active' : ''}`}>
              <img src={slide.src} alt={slide.alt} />
            </div>
          ))}
        </div>
        <div className="hero-overlay" />

        <div className="hero-content">
          <h1 className="hero-title">
            Train Like a Champion at <span className="highlight">MSKICA Cricket Academy</span>
          </h1>
        </div>

        <div className="slide-indicators">
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              className={`slide-dot ${idx === currentHeroSlide ? 'active' : ''}`}
              onClick={() => setCurrentHeroSlide(idx)}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ====== QUICK HIGHLIGHTS ====== */}
      <section className="highlights-bar">
        <div className="highlight-item">
          <span className="highlight-icon">🎓</span>
          <span className="highlight-value">150+</span>
          <span className="highlight-label">Students Trained</span>
        </div>
        <div className="highlight-item">
          <span className="highlight-icon">🌍</span>
          <span className="highlight-value">Ex-Intl</span>
          <span className="highlight-label">World-class Coaches</span>
        </div>
        <div className="highlight-item">
          <span className="highlight-icon">📋</span>
          <span className="highlight-value">Ages 6–19</span>
          <span className="highlight-label">All Skill Levels</span>
        </div>
        <div className="highlight-item">
          <span className="highlight-icon">📍</span>
          <a
            href="https://maps.app.goo.gl/c2Eq8guNpRv2ZrkNA"
            target="_blank"
            rel="noopener noreferrer"
            className="highlight-value highlight-link"
          >
            Seattle
          </a>
          <span className="highlight-label">Training Center</span>
        </div>
      </section>

      {/* ====== ABOUT US ====== */}
      <section id="about" className="about-section">
        <div className="section-container">
          <span className="section-label">About Us</span>
          <h2 className="section-title">Building Future Cricket Stars in Seattle</h2>
          <p className="section-text">
            MSKICA Seattle Cricket Academy is dedicated to developing young cricketers
            through professional coaching and structured training programs. Led by former
            Indian Test cricketer and BCCI Chief Selector MSK Prasad, the academy brings
            international-level expertise to Seattle's youth.
          </p>
          <div className="about-grid">
            <div className="about-card">
              <div className="about-card-icon">🏏</div>
              <h3>Professional Coaching</h3>
              <p>Expert coaching methods from certified coaches with international and professional cricket experience.</p>
            </div>
            <div className="about-card">
              <div className="about-card-icon">📈</div>
              <h3>Structured Development</h3>
              <p>Progressive programs designed to build skills systematically from fundamentals to advanced match play.</p>
            </div>
            <div className="about-card">
              <div className="about-card-icon">🏆</div>
              <h3>Competitive Opportunities</h3>
              <p>Regular match practice and tournament participation to build game awareness and competitive spirit.</p>
            </div>
            <div className="about-card">
              <div className="about-card-icon">🤝</div>
              <h3>Discipline &amp; Sportsmanship</h3>
              <p>Building character, teamwork, and respect for the game alongside technical cricket skills.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ====== PROGRAMS ====== */}
      <section id="programs" className="programs-section">
        <div className="section-container">
          <span className="section-label">Programs</span>
          <h2 className="section-title">Choose Your Path</h2>
          <p className="section-text">
            Every program is built on a progressive curriculum that develops technique,
            game awareness, fitness, and competitive readiness.
          </p>
          <div className="programs-grid">
            <div className="program-card">
              <div className="program-header">
                <span className="program-age">Ages 6–10</span>
                <h3>Junior Program</h3>
                <p className="program-desc">Introduces young players to cricket fundamentals in a fun, supportive environment.</p>
              </div>
              <div className="program-body">
                <ul className="program-features">
                  <li><span className="check">✓</span> Basic batting techniques</li>
                  <li><span className="check">✓</span> Introductory bowling skills</li>
                  <li><span className="check">✓</span> Catching &amp; fielding</li>
                  <li><span className="check">✓</span> Coordination drills</li>
                  <li><span className="check">✓</span> Mini matches</li>
                </ul>
              </div>
            </div>
            <div className="program-card">
              <div className="program-header">
                <span className="program-age">Ages 11–14</span>
                <h3>Development Program</h3>
                <p className="program-desc">Advanced techniques and game strategies for competitive players.</p>
              </div>
              <div className="program-body">
                <ul className="program-features">
                  <li><span className="check">✓</span> Technical batting drills</li>
                  <li><span className="check">✓</span> Seam &amp; spin bowling</li>
                  <li><span className="check">✓</span> Advanced fielding drills</li>
                  <li><span className="check">✓</span> Game awareness</li>
                  <li><span className="check">✓</span> Match practice</li>
                </ul>
              </div>
            </div>
            <div className="program-card">
              <div className="program-header">
                <span className="program-age">Ages 15–18</span>
                <h3>Elite Performance</h3>
                <p className="program-desc">Prepares players for high-level cricket competitions and league play.</p>
              </div>
              <div className="program-body">
                <ul className="program-features">
                  <li><span className="check">✓</span> Advanced batting techniques</li>
                  <li><span className="check">✓</span> Fast &amp; spin bowling</li>
                  <li><span className="check">✓</span> Tactical match prep</li>
                  <li><span className="check">✓</span> Fitness &amp; conditioning</li>
                  <li><span className="check">✓</span> Video analysis</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="programs-cta">
            <p className="programs-cta-text">
              🏏 Ready to hit it out of the park? Grab your spot before the stumps are drawn! 🎯
            </p>
            <a href="https://www.redmondcowcorner.com/cricketacademyregistration" className="btn-primary-hero" target="_blank" rel="noopener noreferrer">Register Now →</a>
          </div>
        </div>
      </section>

      {/* ====== COACHING TEAM ====== */}
      <section id="coaching-team" className="coaches-section">
        <div className="section-container">
          <span className="section-label">Coaching Team</span>
          <h2 className="section-title">International Experience, Local Impact</h2>
          <p className="section-text">
            Each coach brings first-hand experience from professional and international cricket,
            ensuring world-class training standards.
          </p>
          <div className="coaches-grid">
            {coaches.map((coach, index) => (
              <div className="coach-card" key={index}>
                <h3 className="coach-name">{coach.name}</h3>
                <p className="coach-role">{coach.role}</p>
                <p className="coach-bio">{coach.bio}</p>
                <ul className="coach-focus-list">
                  {coach.tags.map((tag, i) => (
                    <li key={i}>{tag}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== GALLERY SLIDESHOW ====== */}
      <section id="gallery" className="gallery-section">
        <div className="section-container">
          <span className="section-label">Gallery</span>
          <h2 className="section-title">Moments That Inspire</h2>
          <p className="section-text">
            A glimpse into the world of MSKICA — from training sessions to
            memorable moments with cricketing legends.
          </p>

          <div
            className="gallery-slideshow"
            onMouseEnter={() => setGalleryPaused(true)}
            onMouseLeave={() => setGalleryPaused(false)}
          >
            {/* Slides */}
            <div className="gallery-slideshow-track">
              {galleryImages.map((img, idx) => (
                <div
                  className={`gallery-slideshow-slide ${
                    idx === currentGallerySlide ? 'active' : ''
                  }`}
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  role="button"
                  tabIndex={idx === currentGallerySlide ? 0 : -1}
                  onKeyDown={(e) => e.key === 'Enter' && setLightboxIndex(idx)}
                >
                  <img src={img.src} alt={img.alt} loading="lazy" />
                  <div className="gallery-slideshow-overlay" />
                  <div className="gallery-slideshow-quote">
                    <div className="gallery-slideshow-quote-icon">"</div>
                    <p className="gallery-slideshow-quote-text">{img.quote.replace(/^"|"$/g, '')}</p>
                    <span className="gallery-slideshow-quote-author">— {img.author}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation arrows */}
            <button
              className="gallery-slideshow-nav prev"
              onClick={() => setCurrentGallerySlide((currentGallerySlide - 1 + galleryImages.length) % galleryImages.length)}
              aria-label="Previous slide"
            >
              ‹
            </button>
            <button
              className="gallery-slideshow-nav next"
              onClick={() => setCurrentGallerySlide((currentGallerySlide + 1) % galleryImages.length)}
              aria-label="Next slide"
            >
              ›
            </button>

            {/* Progress bar */}
            <div className="gallery-slideshow-progress">
              <div
                className={`gallery-slideshow-progress-bar ${galleryPaused ? 'paused' : ''}`}
                key={currentGallerySlide}
              />
            </div>
          </div>

          {/* Dot indicators */}
          <div className="gallery-slideshow-dots">
            {galleryImages.map((_, idx) => (
              <button
                key={idx}
                className={`gallery-slideshow-dot ${idx === currentGallerySlide ? 'active' : ''}`}
                onClick={() => setCurrentGallerySlide(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Lightbox */}
        {lightboxIndex !== null && (
          <div className="gallery-lightbox" onClick={() => setLightboxIndex(null)}>
            <button className="lightbox-close" onClick={() => setLightboxIndex(null)} aria-label="Close">×</button>
            <button
              className="lightbox-arrow lightbox-prev"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + galleryImages.length) % galleryImages.length); }}
              aria-label="Previous"
            >‹</button>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={galleryImages[lightboxIndex].src} alt={galleryImages[lightboxIndex].alt} />
              <div className="lightbox-caption">
                <p className="lightbox-quote">{galleryImages[lightboxIndex].quote.replace(/^"|"$/g, '')}</p>
                <span className="lightbox-author">— {galleryImages[lightboxIndex].author}</span>
              </div>
            </div>
            <button
              className="lightbox-arrow lightbox-next"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % galleryImages.length); }}
              aria-label="Next"
            >›</button>
          </div>
        )}
      </section>

      {/* ====== FOOTER ====== */}
      <footer className="homepage-footer">
        <div className="footer-about">
          <p>Where passion meets precision — MSKICA Seattle Cricket Academy nurtures young cricketers</p>
          <p>with world-class coaching, building confidence, discipline, and a love for the game.</p>
          <p>From grassroots to greatness, we shape the future of cricket in the Pacific Northwest.</p>
        </div>
        <div className="footer-bottom">
          &copy; {new Date().getFullYear()} MSKICA Seattle Cricket Academy. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
